import _extends from "@babel/runtime/helpers/extends";
import { ComponentHeadingSource, getComponentHeadingSource } from '@wix/a11y-adapters';
import { getHTagNumber, HeadingStructureFailedReason, headingStructureRule, parseEntityItemId, RuleId } from '@wix/a11y-rules';
import { Text, TextButton } from '@wix/design-system';
import { useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { convertElementStatusToBooleanStatus, createHeadingDropdownOptions, ElementStatus, HeadingStructureAIProvider, HeadingStructureRuleFixer as SharedHeadingStructureRuleFixer, makeHeadingElementKey, useDeepCompare, useElementToggle } from '@wix/rules-panel-ui';
import { produce } from 'immer';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DEFAULT_HTML_TAG_VIDEO from '../../../assets/MP4/HTML_Tag.mp4';
import DEFAULT_SEO_A11Y_VIDEO from '../../../assets/MP4/SEO_a11y.mp4';
import { currentElementInfoAtom } from '../../../atoms/bi-atoms';
import { headingStructureAIAtomFamily } from '../../../atoms/heading-structure-ai-atoms';
import { ruleApplyTriggeredAtom } from '../../../atoms/session-atoms';
import { BiCtaNames, BiFieldNames, generalToolTips } from '../../../bi/types';
import { usePanelStateAtom } from '../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useGenericElement } from '../../../hooks/use-generic-element';
import { useOpenArticleByEditorHost } from '../../../hooks/use-open-article-by-editor-host';
import { useSessionInfoContext } from '../../../hooks/use-session-info-context';
import { PanoramaErrorNames, PanoramaTransactions } from '../../../panorama/constants';
import { usePanoramaFunctions } from '../../../panorama/use-panorama-functions';
import { headingStructureOnApply } from '../../../rule-mappers/page-level-rules/heading-structure-on-apply';
import { useRuleBase } from '../../../rule-mappers/shared';
import { useGlobalSectionState } from '../../../rule-mappers/shared/use-global-section-state';
import { getEditorCapabilitiesForHost } from '../../../utils/editor-host-to-editor-type';
import { getCompRefType } from '../../../utils/get-comp-ref-type';
import { getRuleElementPropsFromCapabilities } from '../../../utils/get-fixer-design-props';
import { showSaveErrorModal } from '../../modals/show-save-error-modal';
import { convertBooleanStatusToElementStatus, convertScanStatusToUiStatus } from '../alt-image-fixer/utils';
import { HTML_TAG_VIDEO_URL_MAP, SEO_A11Y_VIDEO_URL_MAP } from './heading-structure-rule-content/generic/constants';
import { HeadingStructureRuleGenericContentPopover } from './heading-structure-rule-content/generic/heading-structure-rule-generic-content-popover';
import { useHeadingStructureAI } from './hooks/use-heading-structure-ai';
import { useHeadingStructureAnalytics } from './hooks/use-heading-structure-analytics';
import { findHtmlStringIndex } from './utils/find-html-string-index';
import { getHeadingContent } from './utils/get-heading-content';
const ruleId = RuleId.HEADING_STRUCTURE;
const ruleChecker = headingStructureRule.checker;
export const HeadingStructureRuleFixer = _ref => {
  let {
    htmlStrings,
    failedHeadings,
    failedReasons,
    a11yWizardAdapter,
    editorHost,
    pageId,
    breakpointId,
    itemType
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const {
    locale
  } = useSessionInfoContext();
  const {
    experiments
  } = useExperiments();
  const isFixHeadingStructureWithAiEnabled = experiments.enabled('specs.seo.fixHeadingStructureWithAi');
  const {
    reportErrorPanorama,
    finishPanoramaTransaction,
    startPanoramaTransaction
  } = usePanoramaFunctions();
  const {
    getRuleState
  } = usePanelStateAtom();
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const setRuleApplyTriggered = useSetAtom(ruleApplyTriggeredAtom);
  const {
    updateGlobalSectionStateIfNeeded,
    getGlobalSectionModifiedComponentIds
  } = useGlobalSectionState();
  const {
    collapseElement,
    openNextElement,
    toggleElement,
    selectedElementId
  } = useElementToggle();
  const setCurrentElementInfo = useSetAtom(currentElementInfoAtom);
  const biLogger = useBiLogger();
  const [showLoader, setShowLoader] = useState(false);
  const {
    isGenericElement,
    handleRescanClick
  } = useGenericElement({
    getComponentStyleSource: getComponentHeadingSource,
    pageId,
    unknownValue: ComponentHeadingSource.UNKNOWN
  });
  const isNoMainHeading = failedReasons.includes(HeadingStructureFailedReason.NoMainHeading);
  const isMultipleMainHeading = failedReasons.includes(HeadingStructureFailedReason.MultipleMainHeadings);
  const HTML_TAG_VIDEO = HTML_TAG_VIDEO_URL_MAP[locale == null ? void 0 : locale.toUpperCase()] ?? DEFAULT_HTML_TAG_VIDEO;
  const SEO_A11Y_VIDEO = SEO_A11Y_VIDEO_URL_MAP[locale == null ? void 0 : locale.toUpperCase()] ?? DEFAULT_SEO_A11Y_VIDEO;
  const headingProps = useMemo(() => htmlStrings.map((_ref2, index) => {
    let {
      id,
      uiElementId,
      htmlString,
      attributeId,
      sectionId
    } = _ref2;
    const headingDefinition = getHTagNumber(htmlString);
    const inFailedElement = Boolean(failedHeadings.find(failedHeading => failedHeading.id === uiElementId));
    return {
      attributeId,
      failedReason: isNoMainHeading && index === 0 ? HeadingStructureFailedReason.NoMainHeading : isMultipleMainHeading && headingDefinition === 1 ? HeadingStructureFailedReason.MultipleMainHeadings : inFailedElement ? HeadingStructureFailedReason.HeadingDefinition : undefined,
      headingContent: getHeadingContent(htmlString),
      headingDefinition: headingDefinition ? `H${headingDefinition}` : `p`,
      htmlString,
      id,
      sectionId,
      status: convertBooleanStatusToElementStatus(!(inFailedElement || isNoMainHeading && index === 0 || isMultipleMainHeading && headingDefinition === 1)),
      uiElementId
    };
  }), [failedHeadings, htmlStrings, isMultipleMainHeading, isNoMainHeading]);
  const memoizedHeadingProps = useDeepCompare(headingProps);
  const compactPanelChrome = useMemo(() => getEditorCapabilitiesForHost(editorHost).compactPanelChrome, [editorHost]);
  const {
    capabilities
  } = useA11yWizardAdapter();
  const ruleElementProps = useMemo(() => getRuleElementPropsFromCapabilities(capabilities), [capabilities]);
  const getIsGenericForId = useCallback(id => {
    const compRef = {
      id,
      type: getCompRefType(editorHost, breakpointId)
    };
    return compactPanelChrome ? !(a11yWizardAdapter.isBuilderRichTextComponent != null && a11yWizardAdapter.isBuilderRichTextComponent(compRef)) && isGenericElement(id) : isGenericElement(id);
  }, [a11yWizardAdapter, breakpointId, compactPanelChrome, editorHost, isGenericElement]);
  const {
    manualTaskDataEvent
  } = useHeadingStructureAnalytics({
    headingElements: memoizedHeadingProps,
    pageId
  });
  const openHeadingElement = useCallback(_ref3 => {
    let {
      id: uiElementId
    } = _ref3;
    const selectedElementIndex = memoizedHeadingProps.findIndex(el => el.uiElementId === uiElementId);
    setCurrentElementInfo({
      elementIndex: selectedElementIndex,
      elementStatus: convertElementStatusToBooleanStatus(memoizedHeadingProps[selectedElementIndex].status)
    });
    const {
      entities
    } = parseEntityItemId(uiElementId, 1);
    const compId = entities.length > 0 ? entities[0] : '';
    const nextCompRef = {
      id: compId,
      type: getCompRefType(editorHost, breakpointId)
    };
    if (nextCompRef) {
      a11yWizardAdapter.selectComponent(nextCompRef);
    }
  }, [a11yWizardAdapter, breakpointId, editorHost, memoizedHeadingProps, setCurrentElementInfo]);
  const handleCollapseAnimationEnd = useCallback(() => {
    setShowLoader(false);
  }, [setShowLoader]);
  useEffect(() => {
    manualTaskDataEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    openNextElement(memoizedHeadingProps.map(element => ({
      ...element,
      id: element.uiElementId
    })), openHeadingElement, handleCollapseAnimationEnd);
  }, [handleCollapseAnimationEnd, memoizedHeadingProps, openHeadingElement, openNextElement]);
  const handleClickApply = useCallback(index => async value => {
    const {
      htmlString,
      headingDefinition,
      id,
      uiElementId,
      status,
      attributeId
    } = memoizedHeadingProps[index];
    startPanoramaTransaction(PanoramaTransactions.headingStructureRuleOnApply, {
      headingDefinition,
      htmlString,
      id,
      index,
      status,
      value
    });
    biLogger.taskElementCtaClick({
      ctaName: BiCtaNames.Apply,
      index,
      isManual: getIsGenericForId(memoizedHeadingProps[index].id),
      status
    });
    biLogger.taskElementApplyClick({
      index,
      status,
      value
    });
    setShowLoader(true);
    try {
      var _getRuleState;
      const newHeading = await headingStructureOnApply(value, headingDefinition, htmlString, id, uiElementId, a11yWizardAdapter, editorHost, isFixHeadingStructureWithAiEnabled, attributeId);
      const ruleState = (_getRuleState = getRuleState(pageId, ruleId)) == null ? void 0 : _getRuleState.state;
      const stateHtmlString = (ruleState == null ? void 0 : ruleState.htmlStrings) ?? htmlStrings;
      const oldHtmlIndex = findHtmlStringIndex(stateHtmlString, newHeading);
      if (oldHtmlIndex !== -1) {
        const updatedHtmlStrings = produce(stateHtmlString, draft => {
          draft[oldHtmlIndex] = newHeading;
        });
        const updatedGlobalSectionDataMap = updateGlobalSectionStateIfNeeded(new Map([[newHeading.id, {
          componentId: newHeading.id,
          htmlString: newHeading.htmlString
        }]]));
        updateRuleStateAndVerifyIfNeeded(pageId, ruleId, {
          htmlStrings: updatedHtmlStrings,
          status: convertScanStatusToUiStatus(ruleChecker({
            htmlStrings: updatedHtmlStrings
          }).status)
        }, getGlobalSectionModifiedComponentIds(updatedGlobalSectionDataMap));
      }
    } catch (error) {
      reportErrorPanorama(error, PanoramaErrorNames.headingStructureOnApply);
      console.error('Failed to update heading structure', error);
      await showSaveErrorModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleTryAgain: () => {
          handleClickApply(index)(value);
        },
        t
      });
      setShowLoader(false);
      return;
    }
    setIsApplyTriggered(true);
    setRuleApplyTriggered({
      pageId,
      ruleId
    });
    finishPanoramaTransaction(PanoramaTransactions.headingStructureRuleOnApply, {
      headingDefinition,
      htmlString,
      id,
      index,
      status,
      value
    });
    setShowLoader(false);
    return true;
  }, [memoizedHeadingProps, startPanoramaTransaction, biLogger, getIsGenericForId, setIsApplyTriggered, setRuleApplyTriggered, finishPanoramaTransaction, a11yWizardAdapter, getRuleState, pageId, htmlStrings, updateGlobalSectionStateIfNeeded, updateRuleStateAndVerifyIfNeeded, getGlobalSectionModifiedComponentIds, reportErrorPanorama, editorHost, isFixHeadingStructureWithAiEnabled, t]);
  const handleClickCancel = useCallback(index => () => {
    biLogger.taskElementCtaClick({
      ctaName: BiCtaNames.Cancel,
      index,
      isManual: getIsGenericForId(memoizedHeadingProps[index].id),
      status: memoizedHeadingProps[index].status
    });
    collapseElement();
  }, [biLogger, collapseElement, getIsGenericForId, memoizedHeadingProps]);
  const {
    generatedElements
  } = useAtomValue(headingStructureAIAtomFamily(pageId));
  const headings = useMemo(() => {
    return memoizedHeadingProps.map(el => {
      var _generatedElements$ge;
      const compRef = {
        id: el.id,
        type: getCompRefType(editorHost, breakpointId)
      };
      const isGeneric = compactPanelChrome ? !(a11yWizardAdapter.isBuilderRichTextComponent != null && a11yWizardAdapter.isBuilderRichTextComponent(compRef)) && isGenericElement(el.id) : isGenericElement(el.id);
      const aiProposedTag = (_generatedElements$ge = generatedElements.get(makeHeadingElementKey(el.id, el.attributeId, el.uiElementId))) == null ? void 0 : _generatedElements$ge.proposedTag;
      return {
        aiProposedDefinition: isGeneric ? aiProposedTag : undefined,
        attributeId: el.attributeId ?? undefined,
        componentId: el.id,
        content: el.headingContent,
        elementId: el.uiElementId,
        failedReason: el.failedReason,
        headingDefinition: isGeneric ? el.headingDefinition ?? 'p' : aiProposedTag ?? el.headingDefinition ?? 'p',
        htmlString: el.htmlString,
        isGeneric,
        sectionId: el.sectionId ?? undefined,
        status: el.status
      };
    });
  }, [a11yWizardAdapter, breakpointId, compactPanelChrome, editorHost, generatedElements, isGenericElement, memoizedHeadingProps]);
  const {
    aiProviderValue,
    onUserOverrideTag
  } = useHeadingStructureAI({
    a11yWizardAdapter,
    biLogger,
    editorHost,
    getRuleState,
    headings,
    htmlStrings,
    itemType,
    memoizedHeadingProps,
    pageId,
    updateRuleStateAndVerifyIfNeeded
  });
  const texts = useMemo(() => {
    const dropdownOptions = createHeadingDropdownOptions({
      h1: t('accessibility-wizard.rules-list.heading-definition.dropdown-field.option.h1'),
      h2: t('accessibility-wizard.rules-list.heading-definition.dropdown-field.option.h2'),
      h3: t('accessibility-wizard.rules-list.heading-definition.dropdown-field.option.h3'),
      h4: t('accessibility-wizard.rules-list.heading-definition.dropdown-field.option.h4'),
      h5: t('accessibility-wizard.rules-list.heading-definition.dropdown-field.option.h5'),
      h6: t('accessibility-wizard.rules-list.heading-definition.dropdown-field.option.h6'),
      p: t('accessibility-wizard.rules-list.heading-definition.dropdown-field.option.p')
    });
    return {
      ai: {
        aiMistakesText: t('accessibility-wizard.rules-list.heading-definition.ai.notification-footer.ai-disclaimer'),
        appliedTitle: count => t('accessibility-wizard.rules-list.heading-definition.ai.notification.applied-title', {
          count
        }),
        applyLabel: t('accessibility-wizard.rules-list.heading-definition.ai.notification.apply-label'),
        fixWithAiButtonLabel: t('accessibility-wizard.rules-list.heading-definition.ai.fix-with-ai-button.label'),
        fixWithAiButtonTooltip: t('accessibility-wizard.rules-list.heading-definition.ai.fix-with-ai-button.disabled-tooltip'),
        fixWithAiCancelLabel: t('accessibility-wizard.rules-list.heading-definition.ai.fix-with-ai-button.cancel-label'),
        fixedTitle: count => t('accessibility-wizard.rules-list.heading-definition.ai.notification.fixed-title', {
          count
        }),
        manualTitle: count => t('accessibility-wizard.rules-list.heading-definition.ai.notification.manual-title', {
          count
        }),
        showMoreLabel: t('accessibility-wizard.rules-list.heading-definition.ai.notification.show-more-label'),
        undoTooltip: t('accessibility-wizard.rules-list.heading-definition.ai.undo-tooltip.label')
      },
      applyButtonLabel: t('accessibility-wizard.rules-list.heading-definition.dropdown-field.footer-button.apply'),
      cancelButtonLabel: t('accessibility-wizard.rules-list.heading-definition.dropdown-field.footer-button.cancel'),
      dropdownLabel: t('accessibility-wizard.rules-list.heading-definition.dropdown-field.label'),
      dropdownOptions,
      errors: {
        [HeadingStructureFailedReason.HeadingDefinition]: t('accessibility-wizard.rules-list.heading-definition.errors.HeadingDefinition'),
        [HeadingStructureFailedReason.MultipleMainHeadings]: t('accessibility-wizard.rules-list.heading-definition.errors.MultipleMainHeadings'),
        [HeadingStructureFailedReason.NoMainHeading]: t('accessibility-wizard.rules-list.heading-definition.errors.NoMainHeading')
      },
      genericAiComponentTitle: t('accessibility-wizard.rules-list.heading-definition.ai-section-helper.label'),
      genericAiSuggestion: suggestion => t('accessibility-wizard.rules-list.heading-definition.ai-section-helper.suggestion', {
        suggestion
      }),
      genericBadgeText: t('accessibility-wizard.rules-list.content.generic.badge'),
      genericContentText: t('accessibility-wizard.rules.headingStructure.manualFix.subtitle'),
      iconButtonAriaLabel: t('accessibility-wizard.common.edit.aria-label'),
      iconButtonToolTip: t('accessibility-wizard.common.edit'),
      learnMoreText: t('accessibility-wizard.rules-list.heading-definition.content.generic.learn-more'),
      timeline: {
        errorMessageByReason: {
          [HeadingStructureFailedReason.HeadingDefinition]: t('accessibility-wizard.rules-list.heading-definition.errors.generic.HeadingDefinition'),
          [HeadingStructureFailedReason.MultipleMainHeadings]: t('accessibility-wizard.rules-list.heading-definition.errors.generic.MultipleMainHeadings'),
          [HeadingStructureFailedReason.NoMainHeading]: t('accessibility-wizard.rules-list.heading-definition.errors.generic.NoMainHeading')
        },
        generatedState: {
          firstStep: t('accessibility-wizard.rules-list.heading-definition.timeline.ai-generated.first-step'),
          thirdStep: t('accessibility-wizard.rules-list.heading-definition.timeline.ai-generated.third-step')
        },
        successMessage: t('accessibility-wizard.rules-list.heading-definition.success.generic'),
        timeline: {
          failedByReason: {
            [HeadingStructureFailedReason.NoMainHeading]: [t('accessibility-wizard.rules-list.heading-definition.timeline.second-step.NoMainHeading'), t('accessibility-wizard.rules-list.heading-definition.timeline.first-step.first-part'), t('accessibility-wizard.rules-list.heading-definition.timeline.first-step.second-part'), t('accessibility-wizard.rules-list.heading-definition.timeline.third-step')],
            [HeadingStructureFailedReason.MultipleMainHeadings]: [t('accessibility-wizard.rules-list.heading-definition.timeline.second-step.MultipleMainHeadings'), t('accessibility-wizard.rules-list.heading-definition.timeline.first-step.first-part'), t('accessibility-wizard.rules-list.heading-definition.timeline.first-step.second-part'), t('accessibility-wizard.rules-list.heading-definition.timeline.third-step')],
            [HeadingStructureFailedReason.HeadingDefinition]: [t('accessibility-wizard.rules-list.heading-definition.timeline.second-step.HeadingDefinition'), t('accessibility-wizard.rules-list.heading-definition.timeline.first-step.first-part'), t('accessibility-wizard.rules-list.heading-definition.timeline.first-step.second-part'), t('accessibility-wizard.rules-list.heading-definition.timeline.third-step')]
          }
        }
      }
    };
  }, [t]);
  const timelineTransComponents = useMemo(() => {
    const selectedHeading = headings.find(h => h.elementId === selectedElementId);
    const selectedIndex = selectedHeading ? headings.indexOf(selectedHeading) : 0;
    const selectedStatus = (selectedHeading == null ? void 0 : selectedHeading.status) ?? ElementStatus.Failed;
    return {
      boldText: /*#__PURE__*/React.createElement(Text, {
        size: "tiny",
        weight: "bold"
      }),
      rescanButton: /*#__PURE__*/React.createElement(TextButton, {
        size: "tiny",
        skin: "dark",
        underline: "always",
        weight: "bold",
        onClick: () => {
          if (selectedHeading) {
            handleRescanClick(selectedHeading.componentId);
            biLogger.taskElementCtaClick({
              ctaName: generalToolTips.rescanPage,
              index: selectedIndex,
              isManual: selectedHeading.isGeneric,
              status: selectedHeading.status
            });
          }
        }
      }),
      textButton: /*#__PURE__*/React.createElement(TextButton, {
        size: "tiny",
        skin: "dark",
        underline: "always",
        weight: "thin",
        onClick: () => openSelectedArticle(editorHost, 'accessibility-wizard.rules-list.heading-definition.manual-fix.learn-more.article')
      }),
      textButtonForHTML: /*#__PURE__*/React.createElement(HeadingStructureRuleGenericContentPopover, {
        index: selectedIndex,
        isSeo: false,
        status: selectedStatus,
        videoPath: HTML_TAG_VIDEO
      }),
      textButtonForSEO: /*#__PURE__*/React.createElement(HeadingStructureRuleGenericContentPopover, {
        index: selectedIndex,
        status: selectedStatus,
        videoPath: SEO_A11Y_VIDEO,
        isSeo: true
      })
    };
  }, [biLogger, editorHost, handleRescanClick, headings, openSelectedArticle, selectedElementId, HTML_TAG_VIDEO, SEO_A11Y_VIDEO]);
  const onElementEvent = useCallback(async params => {
    const {
      action,
      componentId,
      elementId,
      elementIndex,
      elementStatus,
      isOpen,
      value
    } = params;
    switch (action) {
      case 'toggle':
        {
          if (isOpen) {
            setCurrentElementInfo({
              elementIndex,
              elementStatus: convertElementStatusToBooleanStatus(elementStatus)
            });
          }
          const compRef = {
            id: componentId,
            type: getCompRefType(editorHost, breakpointId)
          };
          toggleElement(elementId, () => a11yWizardAdapter.selectComponent(compRef))();
          break;
        }
      case 'click':
        {
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.Edit,
            index: elementIndex,
            isManual: getIsGenericForId(componentId),
            status: elementStatus
          });
          break;
        }
      case 'apply':
        {
          const applied = await handleClickApply(elementIndex)(value);
          if (applied) {
            onUserOverrideTag(elementIndex);
          }
          break;
        }
      case 'cancel':
        {
          handleClickCancel(elementIndex)();
          break;
        }
      case 'statusChange':
        {
          biLogger.elementStatusChange({
            index: elementIndex,
            isManual: getIsGenericForId(componentId),
            status: elementStatus,
            value
          });
          break;
        }
      case 'fieldChange':
        {
          if (value !== undefined) {
            biLogger.elementFieldChange({
              fieldName: BiFieldNames.HeadingTag,
              index: elementIndex,
              isManual: getIsGenericForId(componentId),
              status: elementStatus,
              value
            });
          }
          break;
        }
      case 'dropdownOpen':
        {
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.DropdownClick,
            index: elementIndex,
            isManual: getIsGenericForId(componentId),
            status: elementStatus
          });
          break;
        }
      case 'learnMore':
        {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-heading-tags-manual-fix-help');
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.LearnMore,
            index: elementIndex,
            isManual: getIsGenericForId(componentId),
            status: elementStatus
          });
          break;
        }
    }
  }, [a11yWizardAdapter, biLogger, breakpointId, editorHost, getIsGenericForId, handleClickApply, handleClickCancel, onUserOverrideTag, openSelectedArticle, setCurrentElementInfo, toggleElement]);
  return /*#__PURE__*/React.createElement(HeadingStructureAIProvider, {
    value: aiProviderValue
  }, /*#__PURE__*/React.createElement(SharedHeadingStructureRuleFixer, _extends({
    events: {
      onElementEvent
    },
    headings: headings,
    ruleChecker: ruleChecker,
    selectedElementId: selectedElementId ?? undefined,
    showLoader: showLoader,
    texts: texts,
    timelineTransComponents: timelineTransComponents,
    title: t('accessibility-wizard.rules-list.heading-definition.list.failed-elements-number.heading')
  }, ruleElementProps)));
};
//# sourceMappingURL=heading-structure-rule-fixer.js.map